// Contacts — manage the people the app knows (docs/ideas.md "Contact Book").
// Contacts persist in IndexedDB and can be bound to a detected speaker from the
// session's speaker rail, so a recurring person keeps one name, colour and
// avatar across sessions. Browser-only; nothing leaves this machine.

import { ImagePlus, Pencil, Plus, Trash2, UserRound, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { ReadingColumn } from '@/components/shell/AppShell'
import { ContactAvatar } from '@/components/shell/ContactAvatar'
import { Button } from '@/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import {
  deleteContact,
  upsertContact,
  useContacts
} from '@/lib/contacts/contactStore'
import { fileToAvatarDataUrl } from '@/lib/contacts/avatarImage'
import { CONTACT_COLORS, type Contact } from '@/lib/contacts/types'

export function Contacts() {
  const contacts = useContacts()
  const [editing, setEditing] = useState<Contact | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const confirmName = contacts.find((c) => c.id === confirmId)?.name ?? ''

  function startNew() {
    setEditing({
      id: crypto.randomUUID(),
      name: '',
      color: CONTACT_COLORS[0],
      avatar: null,
      createdAt: Date.now()
    })
  }

  return (
    <ReadingColumn>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-body-sm text-muted-foreground">Contact book</p>
          <h1 className="mt-1 font-serif text-display-md text-text">Contacts</h1>
        </div>
        <Button size="sm" onClick={startNew}>
          <Plus /> Add contact
        </Button>
      </div>

      <p className="mt-2 text-body-sm text-muted-foreground">
        People saved here can be linked to a speaker during a session — their
        name, colour and avatar then follow them across sessions. Stored only in
        this browser.
      </p>

      <div className="mt-6 space-y-2">
        {contacts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <UserRound className="mx-auto size-6 text-muted-foreground" />
            <p className="mt-2 text-body-sm text-muted-foreground">
              No contacts yet. Add someone to reuse them across sessions.
            </p>
          </div>
        ) : (
          contacts.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
            >
              <ContactAvatar name={c.name} color={c.color} avatar={c.avatar} />
              <span className="min-w-0 flex-1 truncate text-body text-text">
                {c.name || 'Unnamed'}
              </span>
              <span className={cn('size-2.5 shrink-0 rounded-full bg-current', c.color)} />
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Edit ${c.name}`}
                onClick={() => setEditing(c)}
              >
                <Pencil />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`Delete ${c.name}`}
                onClick={() => setConfirmId(c.id)}
              >
                <Trash2 />
              </Button>
            </div>
          ))
        )}
      </div>

      {editing && (
        <ContactEditor
          contact={editing}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            void upsertContact(next)
            setEditing(null)
          }}
        />
      )}

      <Dialog
        open={confirmId !== null}
        onOpenChange={(open) => !open && setConfirmId(null)}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Delete contact?</DialogTitle>
            <DialogDescription>
              “{confirmName}” will be removed from this browser. Sessions already
              linked to them keep their text; the link is just cleared. This can’t
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmId) void deleteContact(confirmId)
                setConfirmId(null)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ReadingColumn>
  )
}

function ContactEditor({
  contact,
  onClose,
  onSave
}: {
  contact: Contact
  onClose: () => void
  onSave: (next: Contact) => void
}) {
  const [name, setName] = useState(contact.name)
  const [color, setColor] = useState(contact.color)
  const [avatar, setAvatar] = useState<string | null>(contact.avatar)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function onPickFile(file: File | undefined) {
    if (!file) return
    setBusy(true)
    try {
      setAvatar(await fileToAvatarDataUrl(file))
    } catch {
      // ignore — leave the previous avatar in place
    } finally {
      setBusy(false)
    }
  }

  const canSave = name.trim().length > 0 && !busy

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{contact.name ? 'Edit contact' : 'New contact'}</DialogTitle>
          <DialogDescription>
            Saved in this browser and reusable across sessions.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* avatar + upload */}
          <div className="flex items-center gap-3">
            <ContactAvatar
              name={name || '?'}
              color={color}
              avatar={avatar}
              className="size-12 text-body"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus /> {avatar ? 'Replace' : 'Upload'} image
              </Button>
              {avatar && (
                <Button size="sm" variant="ghost" onClick={() => setAvatar(null)}>
                  <X /> Remove
                </Button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  void onPickFile(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
            </div>
          </div>

          {/* name */}
          <div>
            <label className="text-label text-muted-foreground" htmlFor="contact-name">
              Name
            </label>
            <Input
              id="contact-name"
              value={name}
              placeholder="e.g. Tobias Strebitzer"
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSave) {
                  onSave({ ...contact, name: name.trim(), color, avatar })
                }
              }}
              className="mt-1"
            />
          </div>

          {/* colour */}
          <div>
            <span className="text-label text-muted-foreground">Colour</span>
            <div className="mt-1.5 flex gap-2">
              {CONTACT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={color === c}
                  onClick={() => setColor(c)}
                  className={cn(
                    'grid size-7 place-items-center rounded-full ring-offset-2 ring-offset-background transition',
                    color === c && 'ring-2 ring-current'
                  )}
                >
                  <span className={cn('size-4 rounded-full bg-current', c)} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => onSave({ ...contact, name: name.trim(), color, avatar })}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
